/**
 * Platform promo codes & gift cards: the one map from a refusal code to the sentence a
 * user reads (docs/payment-module/platform-offers-api-contract.md §2).
 *
 *   { "detail": { "code": "PLATFORM_CODE_INVALID", "message": "promo expired" } }
 *
 * The `code` is the contract; the server's `message` is English developer prose and is
 * never shown. `RATE_LIMITED` is the one exception to the envelope: the request-limits
 * middleware answers it as a bare string `detail`, which `apiErrorCode` reads too.
 *
 * The sentences live in the "common" namespace (i18n/dictionaries/common/platformOffers.js)
 * because three planes show them: the signup page (saas), the Billing page (tenant admin)
 * and the Gift & Promo screen (super admin).
 *
 * Pure: no React, no transport. The caller passes its bound `t` for "common".
 */
// Relative: a pure module, resolvable under Jest without a shim.
import { apiErrorCode, extractApiError } from "../lib/apiErrors";

/** Code -> "common" i18n key. Every code of the contract's §2 table has an entry. */
export const PLATFORM_OFFER_ERROR_KEYS = Object.freeze({
  // One answer for every state-dependent refusal, so nobody can enumerate codes: the
  // sentence must not claim WHICH of them it was (unknown, expired, used up, wrong plan…).
  PLATFORM_CODE_INVALID: "platformOffers.error.PLATFORM_CODE_INVALID",
  PLATFORM_CODE_RACE: "platformOffers.error.PLATFORM_CODE_RACE",
  NO_ACTIVE_SUBSCRIPTION: "platformOffers.error.NO_ACTIVE_SUBSCRIPTION",
  NO_STRIPE_CUSTOMER: "platformOffers.error.NO_STRIPE_CUSTOMER",
  PROMO_CODE_EXISTS: "platformOffers.error.PROMO_CODE_EXISTS",
  PROMO_TERMS_INVALID: "platformOffers.error.PROMO_TERMS_INVALID",
  GIFT_CARD_NOT_VOIDABLE: "platformOffers.error.GIFT_CARD_NOT_VOIDABLE",
  GIFT_CARD_VOIDED: "platformOffers.error.GIFT_CARD_VOIDED",
  PAYMENT_PROVIDER_UNAVAILABLE: "platformOffers.error.PAYMENT_PROVIDER_UNAVAILABLE",
  // Stripe did not answer: the code is kept reserved and settled server-side (contract
  // §5.6), so the sentence must not invite a retry — a retry is refused anyway.
  REDEMPTION_OUTCOME_UNKNOWN: "platformOffers.error.REDEMPTION_OUTCOME_UNKNOWN",
  NOT_FOUND: "platformOffers.error.NOT_FOUND",
  RATE_LIMITED: "platformOffers.error.RATE_LIMITED",
});

/** Is this refusal one of the §2 codes? A mixed call (change-plan) routes on it. */
export const isPlatformOfferError = (err) =>
  Object.prototype.hasOwnProperty.call(PLATFORM_OFFER_ERROR_KEYS, apiErrorCode(err));

/**
 * The sentence for a failed platform-offer call, in the reader's language.
 *
 * A known code resolves to its key. Anything else (a 422 field list, a network failure,
 * a payload-shape refusal) goes through `extractApiError`, which never renders developer
 * prose, and then to the caller's screen-level `fallback`.
 *
 * @param {any} err          an axios error (or anything)
 * @param {function} t       translator bound to the "common" namespace
 * @param {string} fallback  screen-level generic message
 * @returns {string}
 */
export function platformOfferErrorMessage(err, t, fallback = "") {
  if (isPlatformOfferError(err)) return t(PLATFORM_OFFER_ERROR_KEYS[apiErrorCode(err)]);
  return extractApiError(err, fallback, t) || fallback;
}
